import { memo, useCallback, useState } from "react";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { useUser } from "@/entities/Auth";
import { AddPaymentForm } from "@/features/_customer/AddPaymentForm";
import { useAppDispatch } from "@/shared/lib/hooks/redux";
import { Button } from "@/shared/ui/Button/Button";
import { Modal } from "@/shared/ui/Modal/Modal";

import { getIsLoading } from "../../model/selectors/subscriptionSelectors";
import { retryPayment } from "../../model/services/retryPayment";

import cls from "./PaymentFailed.module.scss";

interface PaymentFailedProps {
  className?: string;
  onUpdate: () => void;
}

export const PaymentFailed = memo(({ className, onUpdate }: PaymentFailedProps) => {
  const dispatch = useAppDispatch();
  const user = useUser();

  const isLoading = useSelector(getIsLoading);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onRetry = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setIsModalOpen(false);

    const result = await dispatch(retryPayment({ userId: user.id }));

    if (result.meta.requestStatus === "fulfilled") {
      onUpdate();
    }
  }, [onUpdate, dispatch, user, isLoading]);

  return (
    <div className={clsx(cls.PaymentFailed, {}, [className])}>
      <Button isLoading={isLoading} onClick={onOpenModal}>
        RETRY PAYMENT
      </Button>
      <AddPaymentForm onUpdate={onUpdate} buttonText="Update" makeDefault />
      <Modal isOpen={isModalOpen} onClose={onCloseModal}>
        <div className={cls.Modal}>
          <p>Are you sure you want to retry payment?</p>
          <div className={cls.Buttons}>
            <Button className={cls.Button} onClick={onRetry}>
              Yes
            </Button>
            <Button className={clsx(cls.Button, {}, [cls.Cancel])} onClick={onCloseModal}>
              No
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
});
